import { isNil, orderBy } from 'lodash'
import React, { Fragment, useMemo, useState } from 'react'
import { useOutletContext, useParams } from 'react-router'
import useTitles from 'common/hooks/useTitles'
import ApplicationConf from 'conf/ApplicationConf'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import { CardTable, SORT } from 'common/components/datatable/Table'
import { getFullDate } from 'common/utils/DateUtil'
import useActions from 'common/hooks/useActions'
import { STATUS } from 'common/constants/storeConstants'
import { REFRESH } from 'common/components/action/ActionConstant'
import useFetchTimeout from 'common/hooks/useFetchTimeout'
import ReactECharts from 'echarts-for-react'
import { CardTitle } from 'common/components/card/Card'
import ServerInfosCard from './ServerInfosCard'
import Select from 'common/components/form/Select'
import Input from 'common/components/form/Input'

const lastCallsHeader = [
    { key: 'routingKey', value: 'Routing Key' },
    { key: 'requestStart', value: 'Début requête' },
    { key: 'processStart', value: 'Début process' },
    { key: 'endDate', value: 'Fin' },
    { key: 'user', value: 'User' },
]

const InstanceServiceApp = () => {
    const { id, idService } = useParams()
    const [ idServiceSelected, setIdServiceSelected ] = useState(Number(idService))
    const { instance } = useOutletContext()

    const {
        data: { services = [] } = {},
        status,
        reload,
    } = useFetchTimeout(ApplicationConf.dashboard(), undefined, 15000, { skipLoadingState: true })

    const serviceData = useMemo(() => services.find(s => s.instance.id === Number(id) && s.service.id === idServiceSelected), [services, id, idServiceSelected])

    useTitles(() => [{
        label: 'Instance',
        href: '/instance',
    }, {
        label: isNil(instance) ? 'Chargement...' : (instance.name ?? 'Inconnue'),
        href: `/instance/${id}`,
    }, {
        label: `Service ${serviceData?.service?.name}`,
        href: `/instance/${id}/service/${idServiceSelected}`,
    }], [instance, id, serviceData, idServiceSelected])

    useActions(() => [{
        type: REFRESH,
        onClick: reload,
        loading: status === STATUS.LOADING,
    }], [status])

    const options = useMemo(() => ({
        series: serviceData?.allRPC?.map(r => ({
            name: r.rpcName,
            type: 'line',
            data: r.calls,
        })),
        legend: {
            data: serviceData?.allRPC?.map(r => r.rpcName),
        },
        xAxis: [{
            type: 'time',
            axisLabel: {
                formatter: undefined,
            },
        }],
        yAxis: [{
            type: 'value',
            // axisLabel: {
            //     formatter: shortenHumanize,
            // },
            axisLine: { show: true },
            axisTick: { show: true },
            splitLine: { show: true },
            min: 0,
            // max: ({ max }) => max * 1.05,
        }],
        grid: { top: 50, right: 20, bottom: 20, left: 30, height: 410 },
        tooltip: {
            trigger: 'axis',
        },
    }), [serviceData])

    return (
        <Grid container spacing={1}>
            <Grid size={6}>
                <Stack>
                    <ServerInfosCard
                        data={serviceData}
                        headerComponent={(
                            <Grid container justifyContent='flex-end'>
                                <Grid size={6}>
                                    <Select
                                        label='Service'
                                        value={idServiceSelected}
                                        onChange={setIdServiceSelected}
                                        items={services.filter(s => s.instance.id === Number(id)).map(s => ({ value: s.service.id, name: s.service.name }))}
                                        sortKey='name'
                                    />
                                </Grid>
                            </Grid>
                        )}
                    />
                    <Card sx={{ marginTop: 1 }}>
                        <CardTitle title={'Nombre d\'appels sur les 3 dernières heures'} />
                        <CardContent>
                            <ReactECharts
                                option={options}
                                notMerge={true}
                                lazyUpdate={true}
                                style={{ height: 500 }}
                            />
                        </CardContent>
                    </Card>
                </Stack>
            </Grid>
            <Grid size={6}>
                <Stack>
                    {orderBy(serviceData?.allRPC, 'rpcName')?.map(r => (
                        <Fragment key={r.rpcName}>
                            <CardTable
                                title={`Derniers appels sur ${r.rpcName}`}
                                rows={r.lastCalls.map(c => ({
                                    ...c,
                                    requestStart: { value: getFullDate(c.requestStart), sortValue: c.requestStart },
                                    processStart: { value: getFullDate(c.processStart), sortValue: c.processStart },
                                    endDate: { value: getFullDate(c.endDate), sortValue: c.endDate },
                                }))}
                                rowsPerPageDefault={10}
                                headers={lastCallsHeader}
                                defaultSort={{ column: 'requestStart', direction: SORT.DESC }}
                            />
                            <Card sx={{ marginBottom: 1 }}>
                                <CardContent>
                                    <Grid container spacing={1}>
                                        <Grid size={6}>
                                            <Input label='Nb Consumers' value={r.rabbitConsumersCount} disabled error={!r.rabbitConsumersCount} />
                                        </Grid>
                                        <Grid size={6}>
                                            <Input label='Nb Messages' value={r.rabbitMessagesCount} disabled />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Fragment>
                    ))}
                </Stack>
            </Grid>
        </Grid>
    )
}

export default InstanceServiceApp